import { FieldGuideUi } from '@tcc/field-guide';
import { useResolvedUser } from '@tcc/shared-data-lib';
import React, { Suspense, useEffect, useRef } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useBoot } from '../../boot';
import { bootSteps } from '../../boot/bootSteps';
import { useAppRouterLoadVital } from '../../hooks/useAppRouterLoadVital';
import { SpinnerLoading } from '../../components/SpinnerLoading/SpinnerLoading';
import { AUTH_VERIFICATION_PATH, FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY } from '../../constants/firstLoginVideo';
import { ROUTE } from '../../constants/routes';
import { AuthPluginContainer } from '../../containers/AuthPluginContainer';
import { CropPlanningAccessProvider } from '../../contexts/CropPlanningAccessContext';
import { getURLFromHash } from '../../utils/env';
import { injectCookieConsentBanner } from '../../utils/injectCookieConsentBanner';
import { useUserLoggedInConfigs, withUseQueryConfig } from '../../utils/sharedData';
import { markManualLoginRequired, setInitialAuthStatus } from '../../utils/appTotalLoad';
import { getClientId } from '../../utils/url';
import { getUserData, logout } from '../../utils/wam';
import { AuthorizedRoute } from '../AuthorizedRoute';

// Lazily loaded so the authenticated app shell (all plugin containers) is a
// separate chunk that never downloads on the /auth login path.
const ApplicationView = React.lazy(() =>
  import('../ApplicationView').then((m) => ({ default: m.ApplicationView })));

/*
 During react-router's initialization, it calls window.location.replace('#/')
 if we don't have an initial hash.
 During release, we add a <base> tag to the head of the index.html file that specifies
 the commit hash that is to be released. Using replace() with a relative path
 also adds the base path to the end of the url, which we do not want. So, we set the
 hash path manually so react-router does not unintentionally redirect us to the wrong path.
*/
if (window.location.hash === '') {
  window.location.hash = '/';
}

export const AppRouter = () => {
  const recoveredUserError = useRef<unknown>();
  const location = useLocation();
  const { pathname } = location;
  const isOnAuth = pathname.includes(ROUTE.Auth);
  const hasPreso = getUserData();
  const loggedInClientId = hasPreso?.clientId?.toString();

  const bootState = useBoot(bootSteps);

  useEffect(() => {
    injectCookieConsentBanner();
  }, []);

  useEffect(() => {
    if (pathname.startsWith(AUTH_VERIFICATION_PATH)) {
      sessionStorage.setItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY, 'true');
    }
  }, [pathname]);

  // Capture whether a valid session existed at cold load. Used to flag the
  // end-to-end app_total_load vital so dashboards can exclude manual-login loads
  // (the login form's human think-time). Only the first call takes effect.
  useEffect(() => {
    setInitialAuthStatus(Boolean(hasPreso));
  }, [hasPreso]);

  const useResolvedUserArgs = {
    ...useUserLoggedInConfigs,
    config: {
      ...useUserLoggedInConfigs.config,
      skip: isOnAuth || !loggedInClientId
    }
  };
  const {
    user,
    loggedInUser,
    error: userError,
    isLoading,
  } = useResolvedUser(useResolvedUserArgs);

  // Temporary dual-hook call to warm React Query cache while SWR is still in use.
  // Remove this useQuery priming call after SWR is fully removed.
  useResolvedUser(withUseQueryConfig(useResolvedUserArgs));

  const history = useHistory();

  // Build auth redirect — always preserves the current URL so tcc-auth-plugin
  // can document.location.assign() back after login.
  const authRedirect = `${ROUTE.Auth}/login?to=${encodeURIComponent(window.location.href)}`;
  const requestedClientId = getClientId(window.location.href);
  const hasRequestedOtherClientId = Boolean(
    !isOnAuth
    && loggedInClientId
    && requestedClientId
    && requestedClientId !== loggedInClientId
  );
  const shouldRecoverFailedClientId = Boolean(
    loggedInUser
    && userError
    && hasRequestedOtherClientId
  );
  const isRecoveringStaleClientId = Boolean(
    userError && recoveredUserError.current === userError
  );
  const isResolvingRequestedClientId = hasRequestedOtherClientId && !user;

  // Stage 4 gate: the AppRouter spinner shows until both i18n boot and the
  // logged-in user resolve. Track its full duration (boot + user).
  const isRouterLoading = isLoading || bootState.isBooting;
  useAppRouterLoadVital(isRouterLoading);

  // Scope html tag with app name as class to avoid css leaks between plugins.
  useEffect(() => {
    const currentMainApp = Object.values(ROUTE).find(appPath => pathname.includes(appPath));

    if (currentMainApp) {
      const htmlNewClassName = currentMainApp.replace('/', '');

      const htmlTag = document.getElementsByTagName('html')[0];

      // Clear previous classes
      htmlTag.classList.remove(...htmlTag.classList);

      htmlTag.classList.add(htmlNewClassName);
    }
  }, [pathname]);

  useEffect(() => {
    if (!userError) {
      recoveredUserError.current = undefined;
      return;
    }

    if (userError && !isLoading && !isOnAuth) {
      if (recoveredUserError.current === userError) {
        return;
      }

      if (shouldRecoverFailedClientId) {
        recoveredUserError.current = userError;

        if (new URLSearchParams(window.location.search).has('client-id')) {
          const recoveryUrl = new URL(window.location.href);
          recoveryUrl.searchParams.delete('client-id');
          window.history.replaceState(window.history.state, '', recoveryUrl);
          history.replace(location);
        } else {
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete('client-id');
          history.replace({
            ...location,
            search: searchParams.toString() ? `?${searchParams.toString()}` : '',
          });
        }

        return;
      }

      // A session appeared present but was rejected — the user must log in
      // manually, so flag this load out of the clean app_total_load cohort.
      markManualLoginRequired();
      logout();
      history.push(authRedirect);
    }
  }, [userError, isLoading, isOnAuth, location, shouldRecoverFailedClientId, history, authRedirect]);

  if (isRouterLoading || isResolvingRequestedClientId || isRecoveringStaleClientId) {
    return <SpinnerLoading />;
  }

  const fieldGuideUserUuid = loggedInUser?.details?.uuid || '';

  return (
    <Suspense fallback={<SpinnerLoading />}>
      <FieldGuideUi userUUID={fieldGuideUserUuid} />
      <Switch>
        <Route path={ROUTE.Auth} component={AuthPluginContainer} />
        {!isOnAuth && !!hasPreso && (
          <CropPlanningAccessProvider>
            <AuthorizedRoute component={ApplicationView} redirect={authRedirect} isEnabled={!!user} />
          </CropPlanningAccessProvider>
        )}
        <Route path='*'>
          {() => {
            window.location.href = getURLFromHash(authRedirect);
            return null;
          }}
        </Route>
      </Switch>
    </Suspense>
  );
};
